import api from "../api/api";

// ─── in-memory cache ──────────────────────────────────────────────────────────
let cachedDefaults = null;
let defaultsPromise = null;
let cachedPages = null;
let pagesPromise = null;

const SITE_NAME = "Pikko Dubai";
const FALLBACK_TITLE = `${SITE_NAME} — Shop Quality Products in UAE`;
const FALLBACK_DESC =
  "Discover top-quality products at unbeatable prices. Shop electronics, fashion, home goods and more — delivered across the UAE.";

const stripHtml = (html = "") =>
  html
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();

// ─── data fetchers (cached, deduplicated) ─────────────────────────────────────

export const getSeoDefaults = async () => {
  if (cachedDefaults) return cachedDefaults;
  if (defaultsPromise) return defaultsPromise;
  defaultsPromise = api
    .get("/home/seo/settings")
    .then((res) => {
      cachedDefaults = res.data.settings || {};
      return cachedDefaults;
    })
    .catch(() => {
      cachedDefaults = {};
      return {};
    })
    .finally(() => {
      defaultsPromise = null;
    });
  return defaultsPromise;
};

export const getSeoPages = async () => {
  if (cachedPages) return cachedPages;
  if (pagesPromise) return pagesPromise;
  pagesPromise = api
    .get("/home/seo/pages")
    .then((res) => {
      cachedPages = res.data.pages || [];
      return cachedPages;
    })
    .catch(() => {
      cachedPages = [];
      return [];
    })
    .finally(() => {
      pagesPromise = null;
    });
  return pagesPromise;
};

// ─── pure builders — return plain objects, touch no DOM ──────────────────────

export const buildRouteSeo = (path, defaults = {}, pages = []) => {
  const page = pages.find((p) => p.path === path);
  const title = page?.title || defaults.defaultTitle || FALLBACK_TITLE;
  const description =
    page?.description || defaults.defaultDescription || FALLBACK_DESC;
  const keywords = page?.keywords || defaults.defaultKeywords || "";
  const ogImage = page?.ogImage || defaults.ogImage || "";
  const robots = page?.noindex
    ? "noindex,nofollow"
    : defaults.robots || "index,follow";
  const canonical =
    page?.canonicalUrl ||
    (defaults.canonicalBase ? `${defaults.canonicalBase}${path}` : "");

  return {
    title,
    description,
    keywords,
    ogImage,
    ogType: "website",
    robots,
    canonical,
    twitterHandle: defaults.twitterHandle || "",
    siteName: SITE_NAME,
  };
};

export const buildProductSeo = (product, defaults = {}) => {
  if (!product) return null;

  const title = `${product.name || "Product"} | ${defaults.defaultTitle || SITE_NAME}`;
  const rawDesc = stripHtml(product.description || "");
  const description =
    rawDesc.slice(0, 160) || defaults.defaultDescription || FALLBACK_DESC;
  const keywords = [
    product.name,
    product.brand,
    product.category,
    product.subcategory,
    defaults.defaultKeywords,
  ]
    .filter(Boolean)
    .join(", ");
  const ogImage = product.images?.[0] || defaults.ogImage || "";
  const canonical = defaults.canonicalBase
    ? `${defaults.canonicalBase}/product/details/${product.slug}`
    : "";

  return {
    title,
    description,
    keywords,
    ogImage,
    ogType: "product",
    canonical,
    robots: "index,follow",
    twitterHandle: defaults.twitterHandle || "",
    siteName: SITE_NAME,
    structuredProduct: {
      name: product.name,
      description,
      image: ogImage,
      sku: product._id || "",
      brand: product.brand || "",
      price: product.price ? Number(product.price).toFixed(2) : undefined,
      currency: "AED",
      availability: (product.stock || 0) > 0 ? "InStock" : "OutOfStock",
      url: canonical,
    },
  };
};

export const buildCategorySeo = (
  category,
  subcategory,
  defaults = {},
  pages = []
) => {
  const catName = decodeURIComponent(category || "")
    .replace(/-/g, " ")
    .trim();
  const subName = subcategory
    ? decodeURIComponent(subcategory).replace(/-/g, " ").trim()
    : "";

  const pagePath = subcategory
    ? `/products?category=${category}&subCategory=${subcategory}`
    : `/products?category=${category}`;

  const page =
    pages.find((p) => p.path === pagePath) ||
    pages.find((p) => p.path === `/products?category=${category}`);

  const displayName = subName || catName;
  const title =
    page?.title || `${displayName} — Shop Online | ${SITE_NAME}`;
  const description =
    page?.description ||
    `Browse our wide selection of ${displayName} products. Find the best ${displayName} deals with fast UAE delivery at Pikko Dubai.`;
  const keywords =
    page?.keywords ||
    [displayName, catName, subName, defaults.defaultKeywords]
      .filter(Boolean)
      .join(", ");
  const ogImage = page?.ogImage || defaults.ogImage || "";
  const robots = page?.noindex
    ? "noindex,nofollow"
    : defaults.robots || "index,follow";
  const canonical =
    page?.canonicalUrl ||
    (defaults.canonicalBase ? `${defaults.canonicalBase}${pagePath}` : "");

  return {
    title,
    description,
    keywords,
    ogImage,
    ogType: "website",
    robots,
    canonical,
    twitterHandle: defaults.twitterHandle || "",
    siteName: SITE_NAME,
  };
};
